import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import ConfirmDialog from "../components/ConfirmDialog";
import { useConfirm } from "../components/useConfirm";
import { toast } from "sonner";
import { ChevronLeft, Smartphone, Monitor, Send, Save, RotateCcw } from "lucide-react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

/**
 * Designer visual de plantillas — /admin/emails/templates/preview/:id
 *
 * Layout 2 columnas:
 *  - Izquierda: editor (nombre, asunto, header/footer pickers, Quill body)
 *  - Derecha: preview live responsive con switch móvil/escritorio
 *  - Toolbar superior: botones "Enviarme prueba", "Restaurar diseño" (is_system),
 *                       "Guardar" y "Volver".
 */
export default function EmailDesignerPage() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [parts, setParts] = useState({ header: [], footer: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [device, setDevice] = useState("desktop"); // "desktop" | "mobile"
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [sampleValues, setSampleValues] = useState({});

  const [form, setForm] = useState({
    name: "", description: "", subject: "", html: "",
    header_id: "_default_", footer_id: "_default_",
    use_default_header: true, use_default_footer: true,
  });

  // Iter24.d: confirm dialog reutilizable
  const { confirm, dialogProps: confirmDialogProps } = useConfirm();

  // Load template + parts
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [{ data: tpl }, { data: partsData }] = await Promise.all([
          api.get(`/admin/emails/templates/${templateId}`),
          api.get("/admin/emails/parts?is_active=true"),
        ]);
        setTemplate(tpl);
        setForm({
          name: tpl.name, description: tpl.description || "",
          subject: tpl.subject, html: tpl.html,
          header_id: tpl.header_id ? tpl.header_id : (tpl.use_default_header ? "_default_" : "_none_"),
          footer_id: tpl.footer_id ? tpl.footer_id : (tpl.use_default_footer ? "_default_" : "_none_"),
          use_default_header: tpl.use_default_header,
          use_default_footer: tpl.use_default_footer,
        });
        // Init sample_values con placeholders
        const sv = {};
        (tpl.variables || []).forEach((v) => { sv[v] = `[${v}]`; });
        setSampleValues(sv);
        // Sort parts
        const byKind = { header: [], footer: [] };
        (partsData.items || []).forEach((p) => {
          if (p.kind === "header" || p.kind === "footer") byKind[p.kind].push(p);
        });
        setParts(byKind);
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando plantilla");
        navigate("/admin/emails");
      } finally {
        setLoading(false);
      }
    })();
  }, [templateId, navigate]);

  // Variables detectadas en vivo
  const liveVariables = useMemo(() => {
    const re = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
    const found = new Set();
    let m;
    while ((m = re.exec(form.subject || "")) !== null) found.add(m[1]);
    while ((m = re.exec(form.html || "")) !== null) found.add(m[1]);
    return Array.from(found).sort();
  }, [form.subject, form.html]);

  // Debounced preview generation
  useEffect(() => {
    if (!template || !templateId) return;
    const timer = setTimeout(async () => {
      try {
        // Persistir cambios temporales NO; usamos preview directo con valores actuales
        // Para el preview server-side compone con parts reales. Hacemos un dry render
        // client-side aquí y un preview server-side al guardar.
        const sample = { ...sampleValues };
        // Asegurar valores para las vars detectadas
        liveVariables.forEach((v) => { if (!(v in sample)) sample[v] = `[${v}]`; });
        // Composición client-side: tomar header/footer html de la selección actual
        const renderVars = (s) => (s || "").replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, k) => sample[k] != null ? String(sample[k]) : `{{${k}}}`);
        const pickPartHtml = (kind, selection) => {
          if (selection === "_none_") return "";
          if (selection === "_default_") {
            const def = (parts[kind] || []).find((p) => p.is_default);
            return def ? def.html : "";
          }
          const p = (parts[kind] || []).find((p) => p.id === selection);
          return p ? p.html : "";
        };
        const composed = [
          pickPartHtml("header", form.header_id),
          form.html,
          pickPartHtml("footer", form.footer_id),
        ].join("");
        setPreviewHtml(renderVars(composed));
        setPreviewSubject(renderVars(form.subject));
      } catch {
        // silencioso
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [form, sampleValues, parts, template, templateId, liveVariables]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const partFields = {
        header_id: form.header_id === "_default_" || form.header_id === "_none_" ? null : form.header_id,
        use_default_header: form.header_id === "_default_",
        footer_id: form.footer_id === "_default_" || form.footer_id === "_none_" ? null : form.footer_id,
        use_default_footer: form.footer_id === "_default_",
      };
      await api.patch(`/admin/emails/templates/${templateId}`, {
        name: form.name.trim(), description: form.description.trim() || null,
        subject: form.subject, html: form.html, ...partFields,
      });
      toast.success("Plantilla guardada");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error guardando");
    } finally { setSaving(false); }
  }, [templateId, form]);

  const sendTest = useCallback(async () => {
    setSending(true);
    try {
      await save();  // persistir cambios antes del envío
      const { data } = await api.post(`/admin/emails/templates/${templateId}/send-test`, {
        sample_values: sampleValues,
      });
      toast.success(`Email de prueba enviado a ${data.to}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error enviando prueba");
    } finally { setSending(false); }
  }, [templateId, sampleValues, save]);

  const resetToSeed = useCallback(async () => {
    const ok = await confirm({
      title: "Restaurar diseño original",
      description: "¿Restaurar esta plantilla al diseño original del sistema? Se perderán los cambios actuales.",
      confirmLabel: "Sí, restaurar",
      destructive: true,
      testIdPrefix: "designer-reset-confirm",
    });
    if (!ok) return;
    setResetting(true);
    try {
      const { data } = await api.post(`/admin/emails/templates/${templateId}/reset-to-seed`);
      setTemplate(data);
      setForm({
        name: data.name, description: data.description || "",
        subject: data.subject, html: data.html,
        header_id: "_default_", footer_id: "_default_",
        use_default_header: true, use_default_footer: true,
      });
      toast.success("Plantilla restaurada al diseño original");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error restaurando");
    } finally { setResetting(false); }
  }, [templateId, confirm]);

  if (loading) return <div className="p-8 text-sm text-gray-500">Cargando designer…</div>;
  if (!template) return null;

  return (
    <div className="min-h-screen bg-brand-cream-soft" data-testid="email-designer-page">
      {/* Toolbar */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-3 sticky top-0 z-10">
        <Link to="/admin/emails" className="inline-flex items-center text-sm text-gray-700 hover:text-gray-900">
          <ChevronLeft size={16} className="mr-1" /> Emails
        </Link>
        <div className="h-5 w-px bg-gray-300" />
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-widest text-gray-500">Designer</p>
          <p className="text-sm font-medium text-gray-900">{template.name}</p>
        </div>
        {template.is_system && (
          <Button variant="outline" size="sm" className="rounded-sm h-8" disabled={resetting} onClick={resetToSeed} data-testid="designer-reset-btn">
            <RotateCcw size={14} className="mr-1.5" />
            {resetting ? "Restaurando…" : "Restaurar diseño"}
          </Button>
        )}
        <Button variant="outline" size="sm" className="rounded-sm h-8" disabled={sending || saving} onClick={sendTest} data-testid="designer-send-test-btn">
          <Send size={14} className="mr-1.5" /> {sending ? "Enviando…" : "Enviarme prueba"}
        </Button>
        <Button size="sm" className="rounded-sm h-8 bg-brand-burgundy hover:bg-brand-burgundy-light text-white" disabled={saving} onClick={save} data-testid="designer-save-btn">
          <Save size={14} className="mr-1.5" /> {saving ? "Guardando…" : "Guardar"}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
        {/* Editor */}
        <div className="bg-white p-6 border-r border-gray-200 max-h-[calc(100vh-58px)] overflow-y-auto" data-testid="designer-editor-pane">
          <div className="space-y-4">
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-gray-500">Nombre</Label>
              <Input value={form.name} disabled={template.is_system} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm h-9" data-testid="designer-form-name" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-gray-500">Asunto</Label>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="rounded-sm h-9 font-mono text-sm" data-testid="designer-form-subject" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-500">Encabezado</Label>
                <Select value={form.header_id} onValueChange={(v) => setForm({ ...form, header_id: v })}>
                  <SelectTrigger className="rounded-sm h-9" data-testid="designer-form-header"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_default_">Usar default</SelectItem>
                    <SelectItem value="_none_">Sin encabezado</SelectItem>
                    {(parts.header || []).map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}{p.is_default ? " (default)" : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-gray-500">Pie</Label>
                <Select value={form.footer_id} onValueChange={(v) => setForm({ ...form, footer_id: v })}>
                  <SelectTrigger className="rounded-sm h-9" data-testid="designer-form-footer"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_default_">Usar default</SelectItem>
                    <SelectItem value="_none_">Sin pie</SelectItem>
                    {(parts.footer || []).map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}{p.is_default ? " (default)" : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-widest text-gray-500">Cuerpo</Label>
              <div className="border border-gray-300 rounded-sm" data-testid="designer-form-html">
                <ReactQuill
                  theme="snow" value={form.html} onChange={(v) => setForm({ ...form, html: v })}
                  modules={{ toolbar: [
                    [{ header: [1, 2, 3, false] }], ["bold", "italic", "underline", "strike"],
                    [{ color: [] }, { background: [] }], [{ list: "ordered" }, { list: "bullet" }],
                    ["link"], ["clean"],
                  ] }}
                  style={{ minHeight: 280 }}
                />
              </div>
            </div>

            {liveVariables.length > 0 && (
              <div className="bg-gray-50 border border-gray-200 p-3 rounded-sm">
                <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">Variables detectadas — valores sample</p>
                <div className="grid grid-cols-2 gap-2">
                  {liveVariables.map((v) => (
                    <div key={v}>
                      <Label className="text-[10px] text-gray-600 font-mono">{`{{${v}}}`}</Label>
                      <Input
                        value={sampleValues[v] || ""}
                        onChange={(e) => setSampleValues({ ...sampleValues, [v]: e.target.value })}
                        className="rounded-sm h-8 text-xs"
                        data-testid={`designer-sample-${v}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Preview */}
        <div className="bg-gray-100 p-6 max-h-[calc(100vh-58px)] overflow-y-auto" data-testid="designer-preview-pane">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[10px] uppercase tracking-widest text-gray-500">Preview live</p>
            <div className="inline-flex border border-gray-300 rounded-sm bg-white">
              <button
                onClick={() => setDevice("desktop")}
                className={`px-3 py-1.5 text-xs inline-flex items-center gap-1 transition-colors ${device === "desktop" ? "bg-brand-burgundy text-white" : "text-gray-700 hover:bg-gray-50"}`}
                data-testid="designer-device-desktop"
              >
                <Monitor size={13} /> Desktop
              </button>
              <button
                onClick={() => setDevice("mobile")}
                className={`px-3 py-1.5 text-xs inline-flex items-center gap-1 transition-colors ${device === "mobile" ? "bg-brand-burgundy text-white" : "text-gray-700 hover:bg-gray-50"}`}
                data-testid="designer-device-mobile"
              >
                <Smartphone size={13} /> Móvil
              </button>
            </div>
          </div>

          {/* Email envelope */}
          <div className={`mx-auto bg-white shadow-md transition-all ${device === "mobile" ? "max-w-[375px]" : "max-w-[680px]"}`} data-testid="designer-preview-envelope">
            <div className="border-b border-gray-200 px-4 py-3 bg-gray-50">
              <p className="text-[10px] uppercase tracking-widest text-gray-500">De</p>
              <p className="text-xs text-gray-800 font-mono">noreply@procure.justsapiens.com</p>
              <p className="text-[10px] uppercase tracking-widest text-gray-500 mt-2">Asunto</p>
              <p className="text-sm font-medium text-gray-900" data-testid="designer-preview-subject">{previewSubject || "—"}</p>
            </div>
            <div className="overflow-x-auto" data-testid="designer-preview-body">
              <div dangerouslySetInnerHTML={{ __html: previewHtml || "" }} />
            </div>
          </div>
        </div>
      </div>
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
}
